import React, { useEffect, useState } from "react";
import { Grid, Modal, Box, Divider, Button, Autocomplete, TextField, IconButton, Typography, ToggleButtonGroup, ToggleButton, Stack, Snackbar, Alert, Tooltip } from "@mui/material";
import ControlledDataGrid from "src/components/DataGrid/ControlledDataGrid";
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import WorkflowStateComponent from "./WorkflowStateComponent";
import moment from "moment-timezone";
import { convertTime } from "src/utils/time";
import axios from "axios";
import { APIURL } from "../../config";
import { INSTANCE_STATUS, INSTANCE_STATUS_LABELS, READONLY_INSTANCE_STATUSES, EDGE_STATUS } from "./workflowConstants";

export default function ActiveWorkflows(props) {
    const gridId = 74;
    const apiRef = useGridApiRef();

    const workflowTypes = [
        { workflow_id: 1, workflow_type: "Active" },
        { workflow_id: 2, workflow_type: "Blocked" },
        { workflow_id: 3, workflow_type: "All" },
    ];

    const [loading, setLoading] = useState(true);
    const [selectedWorkflowType, setSelectedWorkflowType] = useState(workflowTypes[0]);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedSearchEmployee, setSelectedSearchEmployee] = useState(null);
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [workflowInstances, setWorkflowInstances] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [workflowState, setWorkflowState] = useState(null);
    const [savingInstance, setSavingInstance] = useState(false);
    const [unblocking, setUnblocking] = useState(false);
    const [instanceStartDate, setInstanceStartDate] = useState(moment());
    const [instanceEndDate, setInstanceEndDate] = useState(moment());
    const [instanceStatus, setInstanceStatus] = useState('Active');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [selectedEntity, setSelectedEntity] = useState(null);

    useEffect(() => {
        // The instance fetch filters against the employee/customer lists for
        // non-Admin users, so those must be loaded before the first instance fetch
        const init = async () => {
            const [empList, cusList] = await Promise.all([fetchEmployeeData(), fetchCustomerData()]);
            await fetchInstanceData(empList, cusList);
        };
        init();
    }, [props.comp_id, props.selectedProgramId]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const [empList, cusList] = await Promise.all([fetchEmployeeData(), fetchCustomerData()]);
            await fetchInstanceData(empList, cusList);
        };
        init();
    }, [selectedWorkflowType, selectedCustomer, selectedSearchEmployee]);

    useEffect(() => {
        if (selectedCustomer && !customerList.some((customer) => customer.cus_id === selectedCustomer.cus_id)) {
            setSelectedCustomer(null);
        }
        if (selectedSearchEmployee && !employeeList.some((employee) => employee.emp_id === selectedSearchEmployee.emp_id)) {
            setSelectedSearchEmployee(null);
        }
    }, [customerList, employeeList, selectedCustomer, selectedSearchEmployee]);

    const fetchInstanceData = async (empList = employeeList, cusList = customerList) => {
        await axios
            .get(APIURL + "/workflows/instance", {
                params: {
                    compID: props.comp_id,
                    workflowType: selectedWorkflowType ? selectedWorkflowType.workflow_type : null,
                    customerID: selectedCustomer ? selectedCustomer.cus_id : null,
                    employeeID: selectedSearchEmployee ? selectedSearchEmployee.emp_id : null,
                    program_id: props.selectedProgramId ?? undefined,
                },
            }).then((response) => {
                let instances = response.data;
                if (props.user.type !== 'Admin') {
                    const allowedCustomerIds = cusList.map(cus => cus.cus_id);
                    const allowedEmployeeIds = empList.map(emp => emp.emp_id);
                    instances = instances.filter(inst =>
                        allowedCustomerIds.includes(inst.cus_id) &&
                        allowedEmployeeIds.includes(inst.emp_id)
                    );
                }
                setWorkflowInstances(instances);
            }).catch((error) => {
                console.log(error);
            });

        setLoading(false);
    };

    const fetchEmployeeData = async () => {
        if (props.user.type === 'Admin') {
            return await axios.get(APIURL + '/dropdowns/dropdownType/employeeslist', {
                params: { comp_id: props.user.comp_id }
            }).then((response) => {
                const empList = response.data.map((emp) => { return { emp_id: emp.id, emp_name: emp.employee_name } });
                setEmployeeList(empList);
                return empList;
            }).catch((error) => {
                console.log(error);
                return [];
            });
        } else if (props.user.type.includes('Manager')) {
            return await axios.get(APIURL + "/employees/v2/associated", {
                params: { company_id: props.user.comp_id, employee_id: props.user.emp_id },
            }).then((response) => {
                let associatedEmployees = response.data.directlyManaged.concat(response.data.hr_managed).concat({ emp_id: props.user.emp_id, emp_name: props.user.emp_name });

                associatedEmployees = associatedEmployees.filter((obj, index, self) => {
                    return index === self.findIndex((t) => (
                        t.emp_id === obj.emp_id
                    ));
                }).map((emp) => { return { emp_id: emp.emp_id, emp_name: emp.emp_name ? emp.emp_name : emp.emp_first_name + ' ' + emp.emp_last_name } });

                setEmployeeList(associatedEmployees);
                return associatedEmployees;
            }).catch((error) => {
                console.log(error);
                return [];
            });
        } else {
            const empList = [{ emp_id: props.user.emp_id, emp_name: props.user.emp_name }];
            setEmployeeList(empList);
            return empList;
        }
    };

    const fetchCustomerData = async () => {
        return await axios.get(APIURL + "/customers/v2/associated", {
            params: { company_id: props.user.comp_id, employee_id: props.user.emp_id, type: props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager" },
        }).then((response) => {
            const activeCustomers = response.data.filter((cus) => cus.cus_status === 'Active');
            setCustomerList(activeCustomers);
            return activeCustomers;
        }).catch((error) => {
            console.log(error);
            return [];
        });
    };

    const columns = [
        {
            field: "details",
            headerName: "Details",
            width: 60,
            pinnable: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title="Edit Instance">
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => {
                            setEditMode(true);
                            setSelectedEmployee(employeeList.find((emp) => emp.emp_id === params.row.wf_instanceEmployeeID));
                            setInstanceStartDate(moment(params.row.wf_instanceStartDate));
                            setInstanceEndDate(moment(params.row.wf_instanceEndDate));
                            setInstanceStatus(params.row.wf_instanceStatus === INSTANCE_STATUS.CLOSED ? 'Close' : params.row.wf_instanceStatus === INSTANCE_STATUS.PAUSED ? 'Pause' : 'Active');
                            setWorkflowState(workflowInstances.find((wf) => wf.wf_instanceID === params.row.wf_instanceID));
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
        { field: "wf_instanceID", headerName: "ID", width: 60 },
        { field: "wf_title", headerName: "Title", width: 150 },
        { field: "wf_description", headerName: "Description", width: 200 },
        { field: "wf_version", headerName: "Version", width: 65 },
        { field: "instanceStatus", headerName: "Status", width: 100 },
        { field: "wf_nextAction", headerName: "Next Action", width: 150 },
        {
            field: "wf_blockedReason",
            headerName: "Blocked Reason",
            width: 260,
            renderCell: (params) => (
                params.value
                    ? <Tooltip title={params.value}><span style={{ color: '#d32f2f' }}>{params.value}</span></Tooltip>
                    : ''
            ),
        },
        { field: "wf_instanceStartDate", headerName: "Start On", width: 100 },
        { field: "wf_instanceEndDate", headerName: "Ends On", width: 100 },
        { field: "wf_instanceCustomer", headerName: "Customer", width: 150 },
        { field: "wf_instanceEmployee", headerName: "Employee", width: 150 },
        { field: "wf_instanceCreatedAt", headerName: "Created At", width: 100 },
    ];

    // The edge the orchestrator stopped on, if this instance is blocked.
    const getBlockedEdge = (workflow) => (workflow.edgeData || []).find(edge => edge.edge_status === EDGE_STATUS.BLOCKED) || null;

    const getNextWorkflowAction = (workflow) => {
        const nodes = JSON.parse(workflow.wf_nodes);
        const edges = JSON.parse(workflow.wf_edges);
        const nodeData = workflow.nodeData || [];
        const edgeData = workflow.edgeData || [];

        if (workflow.wf_instanceStatus === INSTANCE_STATUS.COMPLETED) {
            return '-';
        }

        const blockedEdge = getBlockedEdge(workflow);
        if (blockedEdge) {
            const edgeDetails = edges.find(e => e.source === blockedEdge.edge_wf_sourceID && e.target === blockedEdge.edge_wf_targetID);
            return edgeDetails ? edgeDetails.data.action : 'Unknown Edge Action';
        }

        if (!edgeData.length && !nodeData.length) {
            return edges.filter(e => e.source === '0')[0]?.data?.action || 'Unknown Edge Action';
        }

        const activeNode = nodeData.find(node => node.node_status === 1);
        if (activeNode) {
            const nodeDetails = nodes.find(n => n.id === activeNode.node_wf_nodeID);
            return nodeDetails ? nodeDetails.data.label : 'Unknown Node Action';
        }

        const activeEdge = edgeData.find(edge => edge.edge_status === EDGE_STATUS.ACTIVE);
        if (activeEdge) {
            const edgeDetails = edges.find(e => e.source === activeEdge.edge_wf_sourceID && e.target === activeEdge.edge_wf_targetID);
            return edgeDetails ? edgeDetails.data.action : 'Unknown Edge Action';
        }

        return '-';
    };

    const rows = workflowInstances.map((data) => {
        const instance = {
            id: data.wf_instanceID,
            wf_instanceWF_ID: data.wf_instanceWF_ID,
            wf_instanceID: data.wf_instanceID,
            wf_title: data.wf_title,
            wf_description: data.wf_description,
            wf_version: data.wf_version,
            instanceStatus: INSTANCE_STATUS_LABELS[data.wf_instanceStatus] || 'Unknown',
            wf_instanceStatus: data.wf_instanceStatus,
            wf_nextAction: getNextWorkflowAction(data),
            wf_blockedReason: getBlockedEdge(data)?.edge_failureReason || '',
            wf_instanceStartDate: moment(data.wf_instanceStartDate).utc().format('MM/DD/YYYY'),
            wf_instanceEndDate: moment(data.wf_instanceEndDate).utc().format('MM/DD/YYYY'),
            wf_instanceCustomerID: data.cus_id,
            wf_instanceCustomer: data.cus_name,
            wf_instanceEmployeeID: data.emp_id,
            wf_instanceEmployee: data.emp_first_name + ' ' + data.emp_last_name,
            wf_instanceCreatedAt: moment(data.wf_instanceCreatedAt).format('MM/DD/YYYY hh:mm A'),
        };
        return instance;
    });

    const DetailItem = ({ label, value }) => (
        <Box>
            <Typography variant="subtitle2" color="text.secondary">
                {label}
            </Typography>
            <Tooltip title={value?.length > 65 && `${value || '-'}`}>
                <Typography variant="body2">
                    {value
                        ? `${value.length > 65 ? value.substring(0, 65) + '...' : value}`
                        : '-'
                    }
                </Typography>
            </Tooltip>
        </Box>
    );

    const formatDate = (date) => {
        return date ? moment(date).format('MM/DD/YYYY hh:mm A') : '-';
    };

    const WorkflowEditForm = () => {
        return (
            <div>
                <Modal open={editMode} onClose={alertMessage ? handleSnackbarAlertClose : handleCloseWfForm}>
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '70%',
                        bgcolor: 'white',
                        borderRadius: 2,
                        boxShadow: 24,
                    }}>
                        <Typography sx={{ textAlign: 'center', p: 2, fontSize: 20 }}>
                            Workflow Instance Journey
                        </Typography>
                        <Divider />
                        <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, height: '70vh' }}>
                                <Box style={{ width: '25%' }}>
                                    <Autocomplete
                                        id="wf-select"
                                        options={[{ wf_id: workflowState.wf_id, wf_title: workflowState.wf_title }]}
                                        value={{ wf_id: workflowState.wf_id, wf_title: workflowState.wf_title }}
                                        getOptionLabel={(option) => option.wf_title}
                                        isOptionEqualToValue={(option, value) => option.wf_id === value.wf_id}
                                        onChange={() => { }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.wf_id}>
                                                {option.wf_title}
                                            </li>
                                        )}
                                        renderInput={(params) => <TextField {...params} label="Select Workflow" />}
                                        sx={{ marginBottom: 3 }}
                                        disabled
                                    />
                                    <Autocomplete
                                        id="client-select"
                                        options={[{ cus_id: workflowState.cus_id, cus_name: workflowState.cus_name }]}
                                        value={{ cus_id: workflowState.cus_id, cus_name: workflowState.cus_name }}
                                        getOptionLabel={(option) => option.cus_name}
                                        isOptionEqualToValue={(option, value) => option.cus_id === value.cus_id}
                                        onChange={() => { }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.cus_id}>
                                                {option.cus_name}
                                            </li>
                                        )}
                                        renderInput={(params) => <TextField {...params} label="Select Customer" />}
                                        sx={{ marginBottom: 3 }}
                                        disabled
                                    />
                                    <Autocomplete
                                        id="employee-select"
                                        options={employeeList}
                                        value={selectedEmployee}
                                        getOptionLabel={(option) => option.emp_name}
                                        isOptionEqualToValue={(option, value) => option.emp_id === value.emp_id}
                                        onChange={(event, value) => { setSelectedEmployee(value); }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.emp_id}>
                                                {option.emp_name}
                                            </li>
                                        )}
                                        renderInput={(params) => <TextField {...params} label="Select Employee" />}
                                        sx={{ marginBottom: 3 }}
                                        disabled={READONLY_INSTANCE_STATUSES.includes(workflowState.wf_instanceStatus)}
                                    />
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="Workflow Start Date"
                                            value={instanceStartDate}
                                            onChange={(newValue) => { setInstanceStartDate(newValue); }}
                                            disablePast
                                            sx={{ width: '100%', marginBottom: 3 }}
                                            disabled={READONLY_INSTANCE_STATUSES.includes(workflowState.wf_instanceStatus)}
                                        />
                                    </LocalizationProvider>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="Workflow End Date"
                                            value={instanceEndDate}
                                            onChange={(newValue) => { setInstanceEndDate(newValue); }}
                                            disablePast
                                            sx={{ width: '100%', marginBottom: 3 }}
                                            disabled={READONLY_INSTANCE_STATUSES.includes(workflowState.wf_instanceStatus)}
                                        />
                                    </LocalizationProvider>
                                    {workflowState.wf_instanceStatus === INSTANCE_STATUS.BLOCKED &&
                                        <Alert
                                            severity="error"
                                            sx={{ marginBottom: 3 }}
                                            action={
                                                <Button
                                                    color="inherit"
                                                    size="small"
                                                    disabled={unblocking}
                                                    onClick={handleUnblock}
                                                >
                                                    {unblocking ? 'Unblocking…' : 'Unblock'}
                                                </Button>
                                            }
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>This workflow is blocked</Typography>
                                            <Typography variant="body2">
                                                {getBlockedEdge(workflowState)?.edge_failureReason || 'A step failed and the workflow was stopped.'}
                                            </Typography>
                                        </Alert>
                                    }
                                    {workflowState.wf_instanceStatus !== INSTANCE_STATUS.BLOCKED &&
                                        !READONLY_INSTANCE_STATUSES.includes(workflowState.wf_instanceStatus) &&
                                        <ToggleButtonGroup
                                            fullWidth
                                            exclusive
                                            value={instanceStatus}
                                            onChange={(event, newStatus) => {
                                                if (newStatus !== null) {
                                                    setInstanceStatus(newStatus);
                                                }
                                            }}
                                        >
                                            <ToggleButton
                                                value="Active"
                                                color="primary"
                                                sx={{
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'primary.main',
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: 'primary.dark',
                                                        },
                                                    },
                                                }}
                                            >
                                                Active
                                            </ToggleButton>
                                            <ToggleButton
                                                value="Pause"
                                                color="warning"
                                                sx={{
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'warning.main',
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: 'warning.dark',
                                                        },
                                                    },
                                                }}
                                            >
                                                {instanceStatus === 'Pause' ? 'Paused' : 'Pause'}
                                            </ToggleButton>
                                            <ToggleButton
                                                value="Close"
                                                color="error"
                                                sx={{
                                                    '&.Mui-selected': {
                                                        backgroundColor: 'error.main',
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: 'error.dark',
                                                        },
                                                    },
                                                }}
                                            >
                                                {instanceStatus === 'Close' ? 'Closed' : 'Close'}
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    }
                                </Box>
                                <Box sx={{ width: '50%', border: 1, borderColor: 'lightgray' }}>
                                    <WorkflowStateComponent workflowState={workflowState} onClick={setSelectedEntity} />
                                </Box>
                                <Box sx={{ width: '25%', p: 2 }}>
                                    {selectedEntity ? (
                                        <Box>
                                            <Typography variant="h6" gutterBottom>
                                                Details
                                            </Typography>

                                            <Divider sx={{ mb: 2 }} />

                                            {selectedEntity.entityType === 'node' ? (
                                                selectedEntity.type === 'decisionNode' ? (
                                                    <Stack spacing={1}>
                                                        <DetailItem label="Node Title" value={selectedEntity?.data?.nodeTitle} />
                                                        <DetailItem label="Node Description" value={selectedEntity?.data?.nodeDescription} />
                                                        <DetailItem label="Attribute" value={selectedEntity?.data?.attributeName} />
                                                        <DetailItem label="Operator" value={selectedEntity?.data?.operator} />
                                                        <DetailItem label="Value" value={selectedEntity?.data?.conditionValue} />
                                                        <DetailItem label="Activated At" value={formatDate(selectedEntity?.node_activatedAt)} />
                                                        <DetailItem label="Completed At" value={formatDate(selectedEntity?.node_completedAt)} />
                                                    </Stack>
                                                ) : selectedEntity.type === 'approvalNode' ? (
                                                    <Stack spacing={1}>
                                                        <DetailItem label="Node Title" value={selectedEntity?.data?.nodeTitle} />
                                                        <DetailItem label="Node Description" value={selectedEntity?.data?.nodeDescription} />
                                                        <DetailItem label="Activated At" value={formatDate(selectedEntity?.node_activatedAt)} />
                                                        <DetailItem label="Completed At" value={formatDate(selectedEntity?.node_completedAt)} />
                                                    </Stack>
                                                ) : (
                                                    <Stack spacing={1}>
                                                        <DetailItem label="Task ID" value={selectedEntity?.tas_id} />
                                                        <DetailItem label="Task" value={selectedEntity?.tas_title} />
                                                        <DetailItem label="Scheduled Start Time" value={convertTime(selectedEntity?.tas_start_time, selectedEntity?.task_emp_timezone, moment.tz.guess(), 'MM/DD/YYYY hh:mm A') ?? formatDate(selectedEntity?.tas_start_time)} />
                                                        <DetailItem label="Scheduled End Time" value={convertTime(selectedEntity?.tas_end_time, selectedEntity?.task_emp_timezone, moment.tz.guess(), 'MM/DD/YYYY hh:mm A') ?? formatDate(selectedEntity?.tas_end_time)} />
                                                        <DetailItem label="Activity Performed" value={selectedEntity?.data?.label} />
                                                        <DetailItem label="Activated At" value={formatDate(selectedEntity?.node_activatedAt)} />
                                                        <DetailItem label="Completed At" value={formatDate(selectedEntity?.node_completedAt)} />
                                                    </Stack>
                                                )
                                            ) : (
                                                <Stack spacing={1}>
                                                    <DetailItem label="Source Task ID" value={selectedEntity?.source_tas_id} />
                                                    <DetailItem label="Source Task" value={selectedEntity?.source_tas_title} />
                                                    <DetailItem label="Source Scheduled Start Time" value={convertTime(selectedEntity?.source_tas_start_time, selectedEntity?.source_task_emp_timezone, moment.tz.guess(), 'MM/DD/YYYY hh:mm A') ?? formatDate(selectedEntity?.source_tas_start_time)} />
                                                    <DetailItem label="Source Scheduled End Time" value={convertTime(selectedEntity?.source_tas_end_time, selectedEntity?.source_task_emp_timezone, moment.tz.guess(), 'MM/DD/YYYY hh:mm A') ?? formatDate(selectedEntity?.source_tas_end_time)} />
                                                    <DetailItem label="Target Task ID" value={selectedEntity?.target_tas_id} />
                                                    <DetailItem label="Target Task" value={selectedEntity?.target_tas_title} />
                                                    <DetailItem label="Target Scheduled Start Time" value={convertTime(selectedEntity?.target_tas_start_time, selectedEntity?.target_task_emp_timezone, moment.tz.guess(), 'MM/DD/YYYY hh:mm A') ?? formatDate(selectedEntity?.target_tas_start_time)} />
                                                    <DetailItem label="Target Scheduled End Time" value={convertTime(selectedEntity?.target_tas_end_time, selectedEntity?.target_task_emp_timezone, moment.tz.guess(), 'MM/DD/YYYY hh:mm A') ?? formatDate(selectedEntity?.target_tas_end_time)} />
                                                    <DetailItem label="Action Performed" value={selectedEntity?.data?.action} />
                                                    <DetailItem label="Activated At" value={formatDate(selectedEntity?.edge_activatedAt)} />
                                                    <DetailItem label="Completed At" value={formatDate(selectedEntity?.edge_completedAt)} />
                                                    {selectedEntity?.edge_failureReason &&
                                                        <DetailItem label="Blocked Reason" value={selectedEntity.edge_failureReason} />
                                                    }
                                                </Stack>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Select a node or edge to see the details.
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                disabled={savingInstance || READONLY_INSTANCE_STATUSES.includes(workflowState.wf_instanceStatus)}
                                sx={{ mt: 3 }}
                            >
                                Save Workflow Instance
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            </div>
        );
    };

    const handleCloseWfForm = () => {
        setEditMode(false);
        setSelectedEntity(null);
    };

    const handleUnblock = async () => {
        setUnblocking(true);

        await axios
            .put(APIURL + `/workflows/instance/unblock`, { wf_instanceID: workflowState.wf_instanceID })
            .then(async () => {
                setAlertMessage('Workflow unblocked — the failed step will be retried on the next run.');
                setAlertType('success');
                setEditMode(false);
                setSelectedEntity(null);
                await fetchInstanceData();
            })
            .catch((error) => {
                console.log(error);
                setAlertMessage(error?.response?.data?.message || 'An error occurred while unblocking the workflow instance!');
                setAlertType('error');
            });

        setUnblocking(false);
    };

    const handleSave = async () => {
        setSavingInstance(true);

        const getInstanceStatus = () => {
            if (workflowState.wf_instanceStatus === INSTANCE_STATUS.BLOCKED) return INSTANCE_STATUS.BLOCKED;
            if (instanceStatus === 'Close') return INSTANCE_STATUS.CLOSED;
            if (instanceStatus === 'Pause') return INSTANCE_STATUS.PAUSED;
            if (workflowState.nodeData || workflowState.edgeData) return INSTANCE_STATUS.IN_PROGRESS;
            return INSTANCE_STATUS.NOT_STARTED;
        };

        await axios
            .put(APIURL + `/workflows/instance`, {
                wf_instanceID: workflowState.wf_instanceID,
                wf_instanceStatus: getInstanceStatus(),
                wf_instanceEmployee: selectedEmployee.emp_id,
                wf_instanceStartDate: moment(instanceStartDate).format('MM-DD-YYYY'),
                wf_instanceEndDate: moment(instanceEndDate).format('MM-DD-YYYY'),
                wf_instanceCreatedBy: props.user.emp_id,
            })
            .then(function (response) {
                setAlertMessage('Workflow instance updated successfully!');
                setAlertType('success');
            })
            .catch(function (error) {
                console.log(error);
                setAlertMessage('An error occurred while updating the workflow instance!');
                setAlertType('error');
            });
    };

    const SnackbarComponent = () => {
        return (
            <div>
                <Snackbar open={alertMessage ? true : false} autoHideDuration={2000} onClose={handleSnackbarAlertClose}>
                    <Alert severity={alertType} elevation={6} variant="filled">
                        {alertMessage}
                        <IconButton
                            sx={{ p: 0.25 }}
                            onClick={handleSnackbarAlertClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
            </div>
        );
    };

    const handleSnackbarAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertMessage('');
        setAlertType('');
        setEditMode(false);
        setSelectedEntity(null);
        setSavingInstance(false);
        fetchInstanceData();
    };

    return (
        <div>
            <div style={{ display: 'flex', marginTop: 10 }}>
                <div style={{ marginRight: 10 }}>
                    <Autocomplete
                        id="wf-select"
                        options={workflowTypes}
                        value={selectedWorkflowType}
                        getOptionLabel={(option) => option.workflow_type}
                        isOptionEqualToValue={(option, value) => option.workflow_id === value.workflow_id}
                        onChange={(event, value) => { setSelectedWorkflowType(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.workflow_id}>
                                {option.workflow_type}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Workflow Type" />}
                        size="small"
                        sx={{ marginBottom: 3, width: 200 }}
                    />
                </div>
                <div style={{ marginRight: 10 }}>
                    <Autocomplete
                        id="client-select"
                        options={customerList}
                        value={selectedCustomer}
                        getOptionLabel={(option) => option.cus_name}
                        isOptionEqualToValue={(option, value) => option.cus_id === value.cus_id}
                        onChange={(event, value) => { setSelectedCustomer(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.cus_id}>
                                {option.cus_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Customer" />}
                        size="small"
                        sx={{ marginBottom: 3, width: 200 }}
                    />
                </div>
                <div style={{ marginRight: 10 }}>
                    <Autocomplete
                        id="employee-select"
                        options={employeeList}
                        value={selectedSearchEmployee}
                        getOptionLabel={(option) => option.emp_name}
                        isOptionEqualToValue={(option, value) => option.emp_id === value.emp_id}
                        onChange={(event, value) => { setSelectedSearchEmployee(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.emp_id}>
                                {option.emp_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Employee" />}
                        size="small"
                        sx={{ marginBottom: 3, width: 200 }}
                    />
                </div>
                <Grid style={{ display: 'flex' }}>
                    <Grid style={{ marginLeft: 40 }} size={1}>
                        <Button
                            onClick={() => { fetchInstanceData(); }}
                            variant="contained"
                        >
                            SEARCH
                        </Button>
                    </Grid>
                </Grid>
            </div>
            <div style={{ height: 'calc(100vh - 210px)', width: '100%' }}>
                <ControlledDataGrid
                    gridId={gridId}
                    user={props.user}
                    apiRef={apiRef}
                    initialState={{
                        sorting: { sortModel: [{ field: "wf_instanceCreatedAt", sort: "desc" }] },
                        pinnedColumns: { left: ['details'] },
                    }}
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        },
                    }}
                    loading={loading}
                />
            </div>
            {editMode && WorkflowEditForm()}
            {alertMessage && SnackbarComponent()}
        </div>
    );
};